import type { FindingCheckRow, FindingListItem, ReportBasis } from './api.ts'
import type { Route } from './route.ts'
import { CHECK_LABEL } from './vocabulary.ts'

/**
 * The list behind a number of the per-check table (#556): which numbers open
 * one, what each is called, and where a row of it leads.
 */

/** The countable columns of the table. Mirrors `CheckCell` in `@rp/core` by hand. */
export enum CheckCell {
  Total = 'total',
  Flagged = 'flagged',
  Corrected = 'corrected',
  NotAnIssue = 'not_an_issue',
  Open = 'open',
  Missed = 'missed',
  Responded = 'responded',
  Agreed = 'agreed',
  Disputed = 'disputed',
  Confirmed = 'confirmed',
  Error = 'error',
  PendingAudit = 'pending_audit',
  PendingConclusion = 'pending_conclusion',
  AwaitingTriage = 'awaiting_triage',
  Unanswered = 'unanswered',
  UnreviewedAgreed = 'unreviewed_agreed',
}

/** The column headings, which are also what the modal says it is listing. */
export const CHECK_CELL_LABEL: Readonly<Record<CheckCell, string>> = {
  [CheckCell.Total]: 'Apontamentos',
  [CheckCell.Flagged]: 'Apontamentos',
  [CheckCell.Corrected]: 'Marcado para correção',
  [CheckCell.NotAnIssue]: 'Não era problema',
  [CheckCell.Open]: 'Em aberto',
  [CheckCell.Missed]: 'Não sinalizamos',
  [CheckCell.Responded]: 'Respostas registradas',
  [CheckCell.Agreed]: 'Concordaram',
  [CheckCell.Disputed]: 'Contestaram',
  [CheckCell.Confirmed]: 'Acertos confirmados',
  [CheckCell.Error]: 'Erros confirmados',
  [CheckCell.PendingAudit]: 'Sem resultado de triagem',
  [CheckCell.PendingConclusion]: 'Não chegaram à triagem',
  [CheckCell.AwaitingTriage]: 'Aguardando triagem',
  [CheckCell.Unanswered]: 'Sem resposta',
  [CheckCell.UnreviewedAgreed]: 'Concordâncias sem validação do apontamento',
}

/** A row's name: the check in Portuguese, or the operational rule by its title. */
export function checkRowName(row: FindingCheckRow): string {
  const check = row.check === null ? 'Sem verificação' : (CHECK_LABEL[row.check] ?? row.check)
  return row.rule === null ? check : (row.ruleTitle ?? row.rule)
}

/**
 * The number a cell shows. The one place it is read off a row, so the table
 * and the list it opens are asked about the same thing.
 */
export function cellCount(row: FindingCheckRow, cell: CheckCell, basis: ReportBasis): number {
  switch (cell) {
    case CheckCell.Total:
      return row.responses.total
    case CheckCell.Responded:
      return row.responses.responded
    case CheckCell.Agreed:
      return row.responses.agreed
    case CheckCell.Disputed:
      return row.responses.disputed
    case CheckCell.UnreviewedAgreed:
      return row.responses.unreviewedAgreed
    case CheckCell.Confirmed:
      return row.responses.confirmed
    case CheckCell.Error:
      return row.responses.errors
    case CheckCell.PendingAudit:
      return row.responses.pendingAudit
    case CheckCell.PendingConclusion:
      return row.responses.pendingConclusion
    case CheckCell.AwaitingTriage:
      return row.responses.awaitingTriage
    case CheckCell.Unanswered:
      return row.responses.unanswered
    case CheckCell.Flagged:
      return row.tally.flagged
    case CheckCell.Corrected:
      return row.tally.corrected
    case CheckCell.NotAnIssue:
      return row.tally.notAnIssue
    case CheckCell.Open:
      return row.tally.answeredOpen + row.tally.unanswered
    case CheckCell.Missed:
      return row.outcome[basis].falseNegatives
  }
}

/** The conferência a listed finding is on, opened on the finding itself. */
export function listedFindingRoute(item: FindingListItem): Route {
  const preparation = item.evidence['preparationId']
  return {
    name: 'verification',
    at: { kind: 'identified', id: item.verificationId },
    finding: item.fingerprint,
    near: {
      ...(typeof preparation === 'string' ? { preparation } : {}),
      ...(item.check === null ? {} : { check: item.check }),
    },
  }
}
